import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeService } from '../services/apiServices';
import {
  Employee,
  EmployeeFilters,
  CreateEmployeeData,
  UpdateEmployeeData,
  UpdateSalaryData,
  PaginatedResponse,
  EmployeeSalaryHistory,
} from '../types';

// Query keys
export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (filters?: EmployeeFilters) => [...employeeKeys.lists(), filters] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: number) => [...employeeKeys.details(), id] as const,
  salaryHistory: (id: number) => [...employeeKeys.all, 'salary-history', id] as const,
};

// Get paginated list of employees
export const useEmployees = (filters?: EmployeeFilters) => {
  return useQuery<PaginatedResponse<Employee>, Error>({
    queryKey: employeeKeys.list(filters),
    queryFn: () => employeeService.getAll(filters),
  });
};

// Get single employee
export const useEmployee = (id: number, enabled = true) => {
  return useQuery<Employee, Error>({
    queryKey: employeeKeys.detail(id),
    queryFn: () => employeeService.getById(id),
    enabled: enabled && !!id,
  });
};

// Get employee salary history
export const useSalaryHistory = (id: number, enabled = true) => {
  return useQuery<EmployeeSalaryHistory[], Error>({
    queryKey: employeeKeys.salaryHistory(id),
    queryFn: () => employeeService.getSalaryHistory(id),
    enabled: enabled && !!id,
  });
};

// Create employee mutation
export const useCreateEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation<Employee, Error, CreateEmployeeData>({
    mutationFn: (data) => employeeService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
};

// Update employee mutation
export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation<Employee, Error, { id: number; data: UpdateEmployeeData }>({
    mutationFn: ({ id, data }) => employeeService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.id) });
    },
  });
};

// Update employee salary mutation
export const useUpdateSalary = () => {
  const queryClient = useQueryClient();

  return useMutation<Employee, Error, { id: number; data: UpdateSalaryData }>({
    mutationFn: ({ id, data }) => employeeService.updateSalary(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.salaryHistory(variables.id) });
    },
  });
};

// Toggle employee status mutation
export const useToggleEmployeeStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<Employee, Error, number>({
    mutationFn: (id) => employeeService.toggleStatus(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) });
    },
  });
};

// Delete employee mutation
export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => employeeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
};
